import { useEffect } from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// Intent-based route prefetch (P2 perf)
//
// Warms a small, curated set of LIGHT public lazy-route chunks when the user
// shows intent (hover or keyboard focus on a link). By the time they click,
// the chunk is already in webpack's cache, so the <Suspense> boundary resolves
// instantly (usually with no visible fallback).
//
// Guardrails honored:
//  • Intent-only — never prefetched on idle/page-load, so the homepage initial
//    payload is unchanged.
//  • No mobile data waste — disabled on touch/coarse pointers and when the
//    browser signals Save-Data.
//  • No heavy datasets — individual guide CONTENT pages (/guides/<slug>, the
//    ~7MB chunk) are intentionally EXCLUDED.
//  • No admin/dashboard prefetch for public users — not registered here.
// ─────────────────────────────────────────────────────────────────────────────

// Import specifiers MUST match the lazy() imports in App.js so webpack dedupes
// to the same chunk (calling these merely warms the existing chunk).
const FACTORIES = {
  login: () => import('@/pages/LoginPage'),
  'features-detail': () => import('@/pages/FeatureDetailPage'),
  guides: () => import('@/pages/GuidesHubPage'),
  'guides-list': () => import('@/pages/AllGuidesListPage'),
  sitemap: () => import('@/pages/SitemapPage'),
  contact: () => import('@/pages/ContactPage'),
  testimonials: () => import('@/pages/TestimonialsPage'),
  // Professional Polish Audit 2026-02 (App-Shell Stage B): these marketing
  // routes moved from eager (main.js) to lazy chunks — warm them on
  // hover/focus intent so the click-through feels instant despite the split.
  personal: () => import('@/pages/PersonalOverviewPage'),
  'personal-pricing': () => import('@/pages/PersonalPricingPage'),
  business: () => import('@/pages/BusinessOverviewPage'),
  'business-pricing': () => import('@/pages/BusinessPricingPage'),
  government: () => import('@/pages/GovernmentOverviewPage'),
  'government-pricing': () => import('@/pages/GovernmentPricingPage'),
  features: () => import('@/pages/FeaturesHubPage'),
  pricing: () => import('@/pages/PricingChooserPage'),
  faq: () => import('@/pages/FAQPage'),
  'how-it-works': () => import('@/pages/HowItWorksPage'),
  'free-trial': () => import('@/pages/FreeTrialPage'),
};

const warmed = new Set();

// Maps a router pathname (lang prefix stripped) to a registry key, or null.
function keyForPath(pathname) {
  const p = pathname.replace(/^\/(ja|en)(?=\/|$)/, '') || '/';
  switch (p) {
    case '/login': return 'login';
    case '/guides': return 'guides';
    case '/guides/list': return 'guides-list';
    case '/sitemap': return 'sitemap';
    case '/contact': return 'contact';
    case '/testimonials': return 'testimonials';
    case '/personal': return 'personal';
    case '/personal/pricing': return 'personal-pricing';
    case '/business': return 'business';
    case '/business/pricing': return 'business-pricing';
    case '/government': return 'government';
    case '/government/pricing': return 'government-pricing';
    case '/features': return 'features';
    case '/pricing': return 'pricing';
    case '/faq': return 'faq';
    case '/how-it-works': return 'how-it-works';
    case '/free-trial': return 'free-trial';
    default:
      // Feature detail pages share one chunk; guide CONTENT pages are excluded.
      if (/^\/features\/[^/]+$/.test(p)) return 'features-detail';
      return null;
  }
}

export function prefetchPath(pathname) {
  const key = keyForPath(pathname);
  if (!key || warmed.has(key)) return;
  const factory = FACTORIES[key];
  if (!factory) return;
  warmed.add(key);
  // Allow a retry if the network fetch fails.
  factory().catch(() => warmed.delete(key));
}

// Warms the primary pricing-chooser chunk once the browser is idle, so a
// visitor who never hovers a pricing link (e.g. arrives via the header CTA)
// still gets an instant transition. Same data-friendly guardrails as hover
// prefetch (skip on touch/coarse pointers + Save-Data).
let idlePricingScheduled = false;
function scheduleIdlePricingPrefetch() {
  if (idlePricingScheduled || typeof window === 'undefined') return;
  idlePricingScheduled = true;
  const run = () => prefetchPath('/pricing');
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(run, { timeout: 4000 });
  } else {
    setTimeout(run, 3000);
  }
}

// One delegated listener for the whole document — zero changes to the many
// <Link> elements across the Header/Footer.
export function useHoverPrefetch() {
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const fine = window.matchMedia('(hover: hover) and (pointer: fine)');
    const conn = navigator.connection;
    if (conn && conn.saveData) return undefined;
    if (fine.matches) scheduleIdlePricingPrefetch();
    if (!fine.matches) return undefined; // touch / mobile → skip hover listeners (data-friendly)

    const handler = (e) => {
      const target = e.target;
      const anchor = target && target.closest ? target.closest('a[href]') : null;
      if (!anchor) return;
      const href = anchor.getAttribute('href');
      if (!href || href[0] !== '/') return; // internal routes only
      prefetchPath(href.split('?')[0].split('#')[0]);
    };

    document.addEventListener('pointerover', handler, { passive: true });
    document.addEventListener('focusin', handler, { passive: true });
    return () => {
      document.removeEventListener('pointerover', handler);
      document.removeEventListener('focusin', handler);
    };
  }, []);
}
